import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import LazyLoad from 'react-lazyload';
import { Box, Container, Typography, Grid, Divider } from '@mui/material';
import { useStyles } from './styles';
import { COLOR_PURPLE_HOVER } from 'styles/variables/colors';
import Faq from '../Faq';
import StepsSection from 'components/StepsSection';
import Infodesign from 'components/Infodesign';
import PricingContent from 'components/PricingContent';
import useIsMobile from 'hooks/useIsMobile';
import Testimonials from 'components/Testimonials';
import { checkUserCountry } from 'utils/locale';
import PricingContentAlternative from 'components/PricingContentAlternative';
import { flowTrialActive } from 'utils';
import { useAuthUser } from 'features/auth/AuthUserContext';

export default function MainContent() {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const { user } = useAuthUser();
  const [userIsChina, setUserIsChina] = useState(false);
  const [userIsJapan, setUserIsJapan] = useState(false);

  async function getCountryFromUser() {
    const isChina = await checkUserCountry.isChina(user);
    const isJapan = await checkUserCountry.isJapan(user);

    setUserIsChina(isChina);
    setUserIsJapan(isJapan);
  }

  useEffect(() => {
    getCountryFromUser();
  }, [user]);

  return (
    <>
      <Box id="pricing-content">
        {flowTrialActive && !(userIsChina || userIsJapan) ? (
          <PricingContentAlternative />
        ) : (
          <PricingContent title="Check Out Our Subscription Plans" />
        )}
      </Box>

      <Box px={6} py={6} bgcolor="#F1F1F1">
        <Container>
          <LazyLoad>
            <Grid container={true} spacing={2} alignItems="center">
              <Grid item={true} xs={12} md={6}>
                <Box minWidth={!isMobile && 686}>
                  <Image
                    src="/images/science-figures/templates.webp"
                    width={670}
                    height={553}
                    alt="Science figures templates"
                    loading="lazy"
                  />
                </Box>
              </Grid>

              <Grid item={true} xs={12} md={6}>
                <Box pl={!isMobile && 14}>
                  <Box pb={3}>
                    <Image
                      src="/images/science-figures/templates-icon.webp"
                      width={60}
                      height={64}
                      alt="templates icon"
                      loading="lazy"
                    />

                    <Typography variant="h5" component="h2" className={classes.primaryColor}>
                      <b>
                        200+ eye-catching, <br /> pre designed templates
                      </b>
                    </Typography>
                  </Box>

                  <Typography variant="h6" component="p">
                    Start your research project faster <br /> by choosing from professionally
                    looking, <br />
                    ready to use scientific templates.
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Box pt={6} pb={3}>
              <Divider />
            </Box>
          </LazyLoad>
        </Container>
      </Box>

      <Box bgcolor="#F1F1F1">
        <Container>
          <LazyLoad>
            <Grid container={true} spacing={2} alignItems="center">
              <Grid item={true} xs={12} md={6}>
                <Box pl={!isMobile && 6}>
                  <Image
                    src="/images/science-figures/illustration-icon.webp"
                    width={51}
                    height={59}
                    alt="illustration icon"
                    loading="lazy"
                  />

                  <Typography variant="h5" component="h2" className={classes.primaryColor}>
                    <b>
                      Visualize any topic with <br /> 75 000+ scientific illustrations
                    </b>
                  </Typography>

                  <Typography variant="h6" component="p">
                    Illustrate any topic or subject you need. <br /> Browse through the world's
                    largest scientifically <br /> accurate illustration gallery - curated by
                    scientists <br /> and researchers.
                  </Typography>
                </Box>
              </Grid>

              <Grid item={true} xs={12} md={6}>
                <Box ml={!isMobile && -12} minWidth={!isMobile && 710}>
                  <Image
                    className={classes.sectionImage}
                    src="/images/science-figures/illustrations.webp"
                    width={708}
                    height={551}
                    alt="Science figures illustrations"
                    loading="lazy"
                  />
                </Box>
              </Grid>
            </Grid>

            <Box py={6}>
              <Divider />
            </Box>
          </LazyLoad>
        </Container>
      </Box>

      <Box bgcolor="#F1F1F1" pb={6}>
        <Container>
          <LazyLoad>
            <Grid container={true} spacing={2} alignItems="center">
              <Grid item={true} xs={12} md={6}>
                <Box minWidth={!isMobile && 710}>
                  <Image
                    src="/images/science-figures/keep-readers.webp"
                    width={708}
                    height={551}
                    alt="Keed readers interested with science figures"
                    loading="lazy"
                  />
                </Box>
              </Grid>

              <Grid item={true} xs={12} md={6}>
                <Box ml={!isMobile && 18}>
                  <Image
                    src="/images/science-figures/keep-readers-icon.webp"
                    width={77}
                    height={56}
                    alt="Keed readers interested icon"
                    loading="lazy"
                  />

                  <Typography variant="h5" component="h2" className={classes.primaryColor}>
                    <b>
                      Keep readers <br /> interested in your work
                    </b>
                  </Typography>

                  <Typography variant="h6" component="p">
                    Use attention grabbing images <br /> rather than boring chunks of text. <br />{' '}
                    Make sure your paper stands out when <br /> people scan through it.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </LazyLoad>
        </Container>
      </Box>

      <Box bgcolor={COLOR_PURPLE_HOVER}>
        <StepsSection
          title="How to create science figures in 4 simple steps?"
          labels={[
            'Sign up to Mind The Graph and start the new project',
            'Pick or customize project size you need to create',
            'Pick a template or start from scratch',
            'Download your science figure work in PNG, PDF, TIFF and other formats',
          ]}
        />
      </Box>

      <Infodesign />

      <Box py={6} bgcolor="#eee">
        <Container>
          <LazyLoad offset={100}>
            <Faq />

            <Box pt={10}>
              <Testimonials showActionButton={false} />
            </Box>
          </LazyLoad>
        </Container>
      </Box>
    </>
  );
}
